import { useT } from "../../i18n/hooks.ts";
import { linkToOutletPoints, geometriesOfLiveEntities } from "../../model/gisLayer.ts";
import {
  assignSurfaceOutletInBulk,
  hasUnreplicatedZones,
  replicateZonesAsOutletPoints,
  type GisColumnMapping,
} from "../../model/projectEntities.ts";
import { TopologyAssignmentSection } from "../TopologyAssignmentSection.tsx";
import { InfoIcon } from "../InfoIcon.tsx";
import { OutletPointsForm, OutletPointsTable } from "../OutletPointCatalog.tsx";
import type { DrawingTool } from "../drawingTools.ts";
import type { EvalHidProject } from "../../model/types.ts";
import { DrawButton } from "./DrawButton.tsx";
import { LinkGisPanel } from "./LinkGisPanel.tsx";
import { useProjectEditing } from "./hooks.ts";

function outletName(project: EvalHidProject, cod: number | null): string {
  return cod == null
    ? "—"
    : (project.outletPoints.find((p) => p.outletCode === cod)?.name ?? `#${cod}`);
}

/**
 * "Outlet points" editor tab: draw on the map, link its own GIS layer
 * (independent of the sub-basins layer), catalog (add/edit/delete) and
 * which outlet point each sub-basin drains into — everything related to
 * this entity in one place, instead of split across the old "GIS layer"/
 * "Draw"/"Outlets and aquifers" tabs.
 */
export function OutletPointsPanel({
  onCreateOutletPointsFromGis,
  drawingMode,
  onStartDrawing,
  onCancelDrawing,
  selection,
  onChangeSelection,
}: {
  onCreateOutletPointsFromGis: (
    geojson: GeoJSON.FeatureCollection,
    mapping: GisColumnMapping
  ) => void;
  drawingMode: DrawingTool | null;
  onStartDrawing: (tool: DrawingTool) => void;
  onCancelDrawing: () => void;
  /** Selection shared with the main map (see EditBar.tsx) — sub-basins are selected by clicking there, not on a separate small map. */
  selection?: Set<number>;
  onChangeSelection?: (selection: Set<number>) => void;
}) {
  const t = useT();
  const {
    project,
    onChangeProject: onChange,
    // SUB-BASINS layer (not its own) — for the assignment map of "which outlet point each sub-basin drains into".
    gisLayer,
    outletPointsLayer,
    onChangeOutletPointsLayer,
  } = useProjectEditing();
  const hasRunoffZones = project.zones.some((z) => z.modelTypeCode != null);
  const canReplicate = hasUnreplicatedZones(project);
  return (
    <div>
      <h3>{t("infoPopover.outletPoints")}</h3>

      {hasRunoffZones && (
        <p>
          <button
            className="button"
            disabled={!canReplicate}
            title={canReplicate ? undefined : t("step3.replicateZonesUpToDate")}
            onClick={() => onChange(replicateZonesAsOutletPoints(project))}
          >
            {t("step3.replicateZonesButton")}
          </button>{" "}
          <InfoIcon text={t("step3.replicateZonesHelp")} />
        </p>
      )}

      <OutletPointsForm project={project} onChange={onChange} />

      <DrawButton
        tool="outletPoint"
        drawingMode={drawingMode}
        onStartDrawing={onStartDrawing}
        onCancelDrawing={onCancelDrawing}
      />

      <details className="collapsible-section" open={outletPointsLayer.linked === 0}>
        <summary>{t("subBasinsPanel.linkGisLayer")}</summary>
        <LinkGisPanel
          entities={project.outletPoints}
          layer={outletPointsLayer}
          link={linkToOutletPoints}
          onLinked={onChangeOutletPointsLayer}
          existingGeometries={geometriesOfLiveEntities(outletPointsLayer, project.outletPoints)}
          onCreate={onCreateOutletPointsFromGis}
          singularName={t("drawing.outletPoint")}
          pluralName={t("common.outletPointPlural")}
        />
      </details>

      <h4>{t("outletPointsPanel.catalog")}</h4>
      <OutletPointsTable project={project} onChange={onChange} />

      {project.outletPoints.length > 0 && (
        <TopologyAssignmentSection
          title={t("outletPointsPanel.topologyTitle")}
          gisLayer={gisLayer}
          project={project}
          isAssigned={(c) => c.outletCode != null}
          valueLabel={(c) => outletName(project, c.outletCode)}
          options={project.outletPoints.map((p) => ({
            value: p.outletCode,
            label: p.name,
          }))}
          buttonText={t("outletPointsPanel.assignOutlet")}
          unassignValue={null}
          onAssign={(codes, value) => onChange(assignSurfaceOutletInBulk(project, codes, value))}
          selection={selection}
          onChangeSelection={onChangeSelection}
        />
      )}
    </div>
  );
}
